import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { RetailerHeaderModel } from '../../../../app/services/api/ofdClient';
import { useGoogleMaps } from '../../../../utils/maps/GoogleMapsProvider';

interface MapPanelProps {
  retailer: RetailerHeaderModel;
}

const RetailerDetailMap: React.FC<MapPanelProps> = ({ retailer }) => {
  const googleMapRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const navigate = useNavigate();
  const { loaded } = useGoogleMaps();

  const initialiseMap = (lat: number, lng: number, zoom: number): void => {
    if (googleMapRef.current) {
      const mapInstance = new window.google.maps.Map(googleMapRef.current, {
        center: { lat, lng },
        zoom,
      });
      mapRef.current = mapInstance;
    }
  };

  const getMarkerIcon = (statusId: number): google.maps.Symbol => {
    const colors = {
      inactive: {
        fill: '#F3F4F6',
        stroke: '#9CA3AF',
      },
      active: {
        fill: '#00B386',
        stroke: '#00583d',
      },
      problem: {
        fill: '#EF4444', 
        stroke: '#B91C1C',
      },
    };
  
    const colorSet =
      statusId === 1 ? colors.inactive :
        statusId === 2 ? colors.active :
          colors.problem;
  
    return {
      path: `M12 2C8.5 2 5.5 5 5.5 8.5c0 5.5 6.5 12.5 6.5 12.5s6.5-7 6.5-12.5C18.5 5 15.5 2 12 2zm0 7.5
        a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z`,
      fillColor: colorSet.fill,
      fillOpacity: 1,
      strokeColor: colorSet.stroke,
      strokeWeight: 1.5,
      scale: 2.0,
      anchor: new google.maps.Point(12, 28),
      labelOrigin: new google.maps.Point(12, 10),
    };
  };
  
  const addMarkers = (): void => {
    if (!mapRef.current) return;

    if (retailer.latitude && retailer.longitude) {
      const marker = new google.maps.Marker({
        position: { lat: retailer.latitude, lng: retailer.longitude },
        map: mapRef.current,
        title: retailer.name,
        icon: getMarkerIcon(retailer.statusId!),
      });

      const infoWindow = new google.maps.InfoWindow({
        content: `
          <div style="
            background: white;
            padding: 16px;
            border-radius: 8px;
            box-shadow: 0 4px 16px rgba(0,0,0,0.08);
            font-family: 'Inter', sans-serif;
            font-size: 14px;
            line-height: 1.5;
            max-width: 240px;
          ">
            <div style="margin-bottom: 8px;">
              <div style="
                font-weight: 600;
                font-size: 16px;
                color: #111827;
                margin-bottom: 2px;
              ">${retailer.name}</div>
              <div style="
                color: #6B7280;
                font-size: 14px;
              ">${retailer.country}</div>
            </div>
          </div>
        `,
      });

      marker.addListener('click', () => {
        infoWindow.open(mapRef.current, marker);
        mapRef.current?.setZoom(15);
        mapRef.current?.setCenter(marker.getPosition() as google.maps.LatLng);

        setTimeout(() => {
          const link = document.getElementById(`view-retailer-${retailer.retailerId}`);
          if (link) {
            link.addEventListener('click', (e) => {
              e.preventDefault();
              navigate(`/retailers/${retailer.retailerId}`);
            });
          }
        }, 0);
      });
    }
  };

  useEffect(() => {
    if (!loaded) return;
    const retailerLat = retailer.latitude ?? 50.5;
    const retailerLng = retailer.longitude ?? 10.0;
    const retailerHasLocation = retailer.longitude != null && retailer.latitude != null;

    initialiseMap(retailerLat, retailerLng, retailerHasLocation ? 10 : 4);
    addMarkers();

  }, [loaded, retailer.retailerId, retailer.latitude]);

  return (
    <>
      {mapRef &&
    <div
      id="map"
      ref={googleMapRef}
      style={{ width: '100%', height: '100%', border: '1px solid #ccc', borderRadius: '12px' }}
    ></div>
      }
    </>
  );
};

export default RetailerDetailMap;